import React, { useState, useEffect } from 'react';
import '../../assets/css/EmailCenter.css';
import EmailService from '../../services/EmailService';
import SmtpConfigService from '../../services/SmtpConfigService';
import ConnectEmailPage from './ConnectEmailPage';
import EmailInboxLayout from './EmailInboxLayout';

const buildId = () => `acc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const loadPersistedAccounts = () => {
  const accounts = [];

  // Restore Gmail session
  const gmailToken = EmailService.getStoredGmailToken();
  const gmailEmail = EmailService.getStoredGmailEmail();
  if (gmailToken) {
    accounts.push({ id: buildId(), type: 'gmail', token: gmailToken, email: gmailEmail, label: 'Gmail' });
  }

  // Restore SMTP sessions stored in localStorage
  const smtpSessions = (() => {
    try { return JSON.parse(localStorage.getItem('smtp_sessions')) || []; } catch { return []; }
  })();
  smtpSessions.forEach((s) => {
    const config = SmtpConfigService.getConfigs().find((c) => c.id === s.configId);
    if (config) {
      accounts.push({ id: s.accountId, type: 'smtp', config, email: config.senderEmail, label: config.name });
    }
  });

  return accounts;
};

const persistSmtpSessions = (accounts) => {
  const sessions = accounts
    .filter((a) => a.type === 'smtp')
    .map((a) => ({ accountId: a.id, configId: a.config.id }));
  localStorage.setItem('smtp_sessions', JSON.stringify(sessions));
};

const EmailCenter = () => {
  const [accounts, setAccounts] = useState([]);
  const [showConnectPage, setShowConnectPage] = useState(false);

  useEffect(() => {
    setAccounts(loadPersistedAccounts());
  }, []);

  const handleAccountConnected = (accountData) => {
    const newAccount = { id: buildId(), ...accountData };

    if (accountData.type === 'gmail') {
      EmailService.storeGmailAuth(accountData.token, accountData.email);
    } else if (accountData.type === 'smtp') {
      EmailService.setActiveSmtpProvider(accountData.email, accountData.config?.id);
    }

    setAccounts((prev) => {
      const filtered = prev.filter(
        (a) => !(a.type === newAccount.type && a.email === newAccount.email)
      );
      const updated = [...filtered, newAccount];
      persistSmtpSessions(updated);
      return updated;
    });
    setShowConnectPage(false);
  };

  const handleDisconnect = (accountId) => {
    setAccounts((prev) => {
      const account = prev.find((a) => a.id === accountId);
      if (account?.type === 'gmail') {
        EmailService.clearStoredEmailAuth();
      }
      const updated = prev.filter((a) => a.id !== accountId);
      persistSmtpSessions(updated);
      return updated;
    });
  };

  if (accounts.length === 0 || showConnectPage) {
    return (
      <ConnectEmailPage
        onConnect={handleAccountConnected}
        onCancel={accounts.length > 0 ? () => setShowConnectPage(false) : null}
      />
    );
  }

  return (
    <EmailInboxLayout
      accounts={accounts}
      onAddAccount={() => setShowConnectPage(true)}
      onDisconnect={handleDisconnect}
    />
  );
};

export default EmailCenter;
